/**
 * The four reports' wire layer — v19 D4 (Figma 06): Project Received · Complete Work ·
 * Billing · Parties.
 *
 * TRANSCRIBED FROM THE SERVICE, NOT GUESSED: every row shape mirrors
 * `apps/api/src/dashboards/reports/dto/reports.dto.ts` field for field.
 *
 * ===========================================================================
 * MONEY CROSSES THIS BOUNDARY — `amount` / `advance_amount`, MASKED PER ROW
 * ===========================================================================
 * A seat without `amounts.view` receives the literal `'•••'` in every money field, never a
 * number. Every cell goes through `ServerAmount`, and the EXPORT serialises the rendered
 * strings (`lib/csv.ts`), so a refused figure exports as the marker — rule 14 on the export
 * channel. `PartiesReportDto.sort` says which sort was APPLIED: a masked seat asking for
 * `sort=amount` is answered with `projects` and the screen says so.
 *
 * `disclosures[]` are SERVED, not merely documented (the tally-licences reports precedent):
 * Billing's honest-subset statement — no invoice entity, so no due date, received amount,
 * overdue or days-to-payment — arrives on the wire and is rendered under the table.
 *
 * `?period=&from=&to=&work_type=` pass through UNVALIDATED — the server's 422 names the
 * vocabulary; silently dropping a stranger value would render the whole report under a URL
 * claiming a filter.
 */

import { ApiRequestError, ApiTransportError, apiRequest } from '../../lib/api';
import type { PeriodQuery } from '../dashboards/periods';

/* ───────────────────────────────────────────────────────────────────── DTOs ── */

export interface Paged<T> {
  data: T[];
  page: number;
  perPage: number;
  /** The SERVER's count over the whole filtered set — never the length of `data`. */
  total: number;
}

export interface ReportWindowDto {
  readonly key: string;
  /** Inclusive 'YYYY-MM-DD'. */
  readonly from: string;
  readonly to: string;
  readonly today: string;
  readonly tz: string;
  readonly fy_start_month: number;
  readonly previous: { readonly from: string; readonly to: string };
  readonly days: number;
}

export type ReportWorkType = 'crf' | 'project' | 'all';
export const REPORT_WORK_TYPES: readonly { value: ReportWorkType; label: string }[] = [
  { value: 'all', label: 'All work' },
  { value: 'crf', label: 'CRFs' },
  { value: 'project', label: 'Projects' },
];

interface ReportRoot {
  readonly country_code: string;
  readonly currency: string;
  readonly work_type: ReportWorkType;
  readonly disclosures: readonly string[];
}

export interface PersonRefDto {
  readonly id: string;
  readonly name: string;
}
export interface PartyRefDto {
  readonly id: string;
  readonly name: string;
}

export interface ReceivedRowDto {
  readonly crf_id: string;
  readonly doc_no: string;
  readonly work_type: 'crf' | 'project';
  readonly title: string;
  readonly status: string;
  readonly party: PartyRefDto & { readonly phone: string | null; readonly email: string | null };
  readonly contact: {
    readonly name: string | null;
    readonly role: string | null;
    readonly phone: string | null;
    readonly email: string | null;
  } | null;
  readonly platforms: readonly string[];
  readonly stacks: readonly string[];
  /** MONEY — `'•••'` for a masked seat. */
  readonly amount: string | null;
  readonly currency: string | null;
  readonly payment_status: string | null;
  readonly received_at: string;
  readonly owner: PersonRefDto | null;
  readonly country_code: string;
}
export interface ReceivedSummaryDto {
  readonly received: number;
  readonly previous: number;
  readonly delta: number;
  readonly per_week: number | null;
  readonly by_status: Readonly<Record<string, number>>;
}
export interface ReceivedReportDto extends ReportRoot, Paged<ReceivedRowDto> {
  readonly window: ReportWindowDto;
  readonly summary: ReceivedSummaryDto;
}

export interface CompleteRowDto {
  readonly crf_id: string;
  readonly doc_no: string;
  readonly work_type: 'crf' | 'project';
  readonly title: string;
  readonly status: string;
  readonly party: PartyRefDto;
  readonly platforms: readonly string[];
  readonly stacks: readonly string[];
  readonly received_at: string;
  readonly completed_at: string;
  readonly duration_days: number;
  readonly developer: PersonRefDto | null;
  readonly deployments: number;
  /** MONEY — `'•••'` for a masked seat. */
  readonly amount: string | null;
  readonly currency: string | null;
  readonly country_code: string;
}
export interface CompleteSummaryDto {
  readonly completed: number;
  readonly previous: number;
  readonly delta: number;
  readonly avg_duration_days: number | null;
}
export interface CompleteReportDto extends ReportRoot, Paged<CompleteRowDto> {
  readonly window: ReportWindowDto;
  readonly summary: CompleteSummaryDto;
}

export interface BillingRowDto {
  readonly crf_id: string;
  readonly doc_no: string;
  readonly work_type: 'crf' | 'project';
  readonly title: string;
  readonly status: string;
  readonly party: PartyRefDto;
  readonly payment_status: string | null;
  /** MONEY — `'•••'` for a masked seat. */
  readonly amount: string | null;
  /** MONEY — `'•••'` for a masked seat. */
  readonly advance_amount: string | null;
  readonly currency: string | null;
  readonly billable_since: string;
  readonly invoice_no: string | null;
  readonly invoice_date: string | null;
  readonly so_doc_no: string | null;
  readonly country_code: string;
}
export interface BillingByPaymentStatusDto {
  readonly payment_status: string | null;
  readonly count: number;
  /** MONEY — `'•••'` for a masked seat; null when no row in the cell has an amount. */
  readonly amount: string | null;
  readonly country_code: string;
}
export interface BillingSummaryDto {
  readonly billable: number;
  readonly unstamped: number;
  readonly by_payment_status: readonly BillingByPaymentStatusDto[];
}
export interface BillingReportDto extends ReportRoot, Paged<BillingRowDto> {
  readonly window: ReportWindowDto;
  readonly summary: BillingSummaryDto;
}

export const PARTY_REPORT_SORTS = ['amount', 'projects', 'name', 'last_activity'] as const;
export type PartyReportSort = (typeof PARTY_REPORT_SORTS)[number];
export const PARTY_SORT_LABEL: Readonly<Record<PartyReportSort, string>> = {
  amount: 'Lifetime value',
  projects: 'Projects',
  name: 'Name',
  last_activity: 'Last activity',
};

export interface PartyRowDto {
  readonly party_id: string;
  readonly name: string;
  readonly city: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly source: string;
  readonly status: string;
  readonly is_group_entity: boolean;
  readonly projects: number;
  /** MONEY — lifetime SUM(crfs.amount); `'•••'` for a masked seat. */
  readonly amount: string | null;
  readonly currency: string;
  readonly last_activity: string;
  readonly country_code: string;
}
export interface PartiesSummaryDto {
  readonly parties: number;
  readonly with_projects: number;
}
export interface PartiesReportDto extends ReportRoot, Paged<PartyRowDto> {
  /** `null` when no `?period=` was supplied — lifetime, unfiltered. */
  readonly window: ReportWindowDto | null;
  /** The sort APPLIED — a masked seat cannot sort by money. */
  readonly sort: PartyReportSort;
  readonly summary: PartiesSummaryDto;
}

/* ───────────────────────────────────────────────────────────────── fetchers ── */

export interface ReportFilters extends PeriodQuery {
  readonly work_type?: string;
  readonly page?: number;
  readonly perPage?: number;
}
export interface PartiesFilters extends ReportFilters {
  readonly q?: string;
  readonly city?: string;
  readonly sort?: string;
}

function baseQuery(filters: ReportFilters): Record<string, string | number | undefined> {
  return {
    period: filters.period,
    from: filters.from,
    to: filters.to,
    work_type: filters.work_type,
    page: filters.page,
    perPage: filters.perPage,
  };
}

export async function fetchReceivedReport(
  cc: string,
  filters: ReportFilters,
  signal?: AbortSignal,
): Promise<ReceivedReportDto> {
  return apiRequest<ReceivedReportDto>(`/countries/${cc}/reports/received`, {
    query: baseQuery(filters),
    signal,
  });
}
export async function fetchCompleteReport(
  cc: string,
  filters: ReportFilters,
  signal?: AbortSignal,
): Promise<CompleteReportDto> {
  return apiRequest<CompleteReportDto>(`/countries/${cc}/reports/complete`, {
    query: baseQuery(filters),
    signal,
  });
}
export async function fetchBillingReport(
  cc: string,
  filters: ReportFilters,
  signal?: AbortSignal,
): Promise<BillingReportDto> {
  return apiRequest<BillingReportDto>(`/countries/${cc}/reports/billing`, {
    query: baseQuery(filters),
    signal,
  });
}
export async function fetchPartiesReport(
  cc: string,
  filters: PartiesFilters,
  signal?: AbortSignal,
): Promise<PartiesReportDto> {
  return apiRequest<PartiesReportDto>(`/countries/${cc}/reports/parties`, {
    query: { ...baseQuery(filters), q: filters.q, city: filters.city, sort: filters.sort },
    signal,
  });
}

/* ────────────────────────────────────────────────────────────────── errors ── */

/** Re-authored per feature, the house pattern: a 403 never names the permission key. */
export function reportsErrorText(error: unknown): string {
  if (error instanceof ApiTransportError) {
    return 'The server could not be reached. Nothing was changed.';
  }
  if (error instanceof ApiRequestError) {
    if (error.code === 'COUNTRY_SCOPE_VIOLATION') return 'That report belongs to another country.';
    if (error.status === 403) return 'This account is not allowed to read that report.';
    return error.message;
  }
  return 'Something went wrong. Nothing was changed.';
}

/* ─────────────────────────────────────────────────────────────── query keys ── */

export const reportKeys = {
  received: (cc: string, f: ReportFilters) => ['reports', cc, 'received', f] as const,
  complete: (cc: string, f: ReportFilters) => ['reports', cc, 'complete', f] as const,
  billing: (cc: string, f: ReportFilters) => ['reports', cc, 'billing', f] as const,
  parties: (cc: string, f: PartiesFilters) => ['reports', cc, 'parties', f] as const,
};
